import { isValidNationalRegisterNumber } from './FormHelper';
import { IFPIEFormData } from './Types/IFPIEFormData';
import { IHandicapFormData, IJeuneFormData, ITuteurFormData } from './Types';
import { IIncitantFormData } from './Types/IIncitantFormData';
import { IIndependantFormData } from './Types/IIndependantFormData';
import { IbanOwnerType } from './Types';

export type ValidationResult<T> = Partial<Record<keyof T, string>>;

type Translations = Record<string, string>;
type Errors<T> = ValidationResult<T>;
const EMAIL_REGEX =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

function trimStr(v: unknown): string {
    return typeof v === 'string' ? v.trim() : '';
}

function setRequiredErrors<T extends object>(
    data: T,
    fields: readonly (keyof T)[],
    errors: Errors<T>,
    translations: Translations,
    messageKey = 'Site.Prime.ErrorSubmit'
) {
    for (const field of fields) {
        const v = data[field];
        if (!v) {
            errors[field] = translations[messageKey] as any;
        }
    }
}

function validateEmailPair<
    T extends { mail: string; mailConfirmation: string },
>(
    data: T,
    errors: Errors<T>,
    translations: Translations,
    opts?: {
        requiredMessageKey?: string;
        invalidMessageKey?: string;
        mismatchMessageKey?: string;
    }
) {
    const requiredKey = opts?.requiredMessageKey ?? 'Site.Prime.ErrorSubmit';
    const invalidKey = opts?.invalidMessageKey ?? 'Site.Prime.MailCompanyError';
    const mismatchKey =
        opts?.mismatchMessageKey ?? 'Site.Prime.MailCompanyConfirmationError';

    const mail = trimStr(data.mail);
    const mailConf = trimStr(data.mailConfirmation);

    if (!mail) {
        errors.mail = translations[requiredKey] as any;
    } else if (!EMAIL_REGEX.test(mail)) {
        errors.mail = translations[invalidKey] as any;
    }

    if (!mailConf) {
        errors.mailConfirmation = translations[requiredKey] as any;
    } else if (mail !== mailConf) {
        errors.mailConfirmation = translations[mismatchKey] as any;
    }
}

function validatePhonePair<
    T extends { phone: string; phoneConfirmation: string },
>(
    data: T,
    errors: Errors<T>,
    translations: Translations,
    opts?: {
        requiredMessageKey?: string;
        mismatchMessageKey?: string;
    }
) {
    const requiredKey = opts?.requiredMessageKey ?? 'Site.Prime.ErrorSubmit';
    const mismatchKey =
        opts?.mismatchMessageKey ?? 'Site.Prime.PhoneConfirmationError';

    const phone = trimStr(data.phone);
    const phoneConf = trimStr(data.phoneConfirmation);

    if (!phone) {
        errors.phone = translations[requiredKey] as any;
    }
    if (!phoneConf) {
        errors.phoneConfirmation = translations[requiredKey] as any;
    } else if (phone && phone !== phoneConf) {
        errors.phoneConfirmation = translations[mismatchKey] as any;
    }
}

function validateIbanRequired<T extends { iban: string }>(
    data: T,
    errors: Errors<T>,
    translations: Translations
) {
    const iban = trimStr(data.iban);
    if (!iban) {
        errors.iban = translations['Site.Prime.ErrorSubmit'] as any;
    }
}

function validateCommissionCompany<T extends { commissionCompany?: string }>(
    data: T,
    errors: Errors<T>,
    translations: Translations
) {
    const digits = (data.commissionCompany ?? '').replace(/\D/g, '');

    if (!digits) {
        errors.commissionCompany = translations[
            'Site.Prime.ErrorSubmit'
        ] as any;
        return;
    }

    if (digits.length > 5) {
        errors.commissionCompany = translations[
            'Site.Prime.IdentificationCodeError'
        ] as any;
    }
}

function validateNiss<T extends Record<string, any>>(
    data: T,
    field: keyof T,
    errors: Errors<T>,
    translations: Translations,
    messageKey = 'Site.Prime.IdentificationCodeError'
) {
    const v = trimStr(data[field]);
    if (!v || !isValidNationalRegisterNumber(v)) {
        errors[field] = translations[messageKey] as any;
    }
}

function validateCheckboxRequired<T extends Record<string, any>>(
    data: T,
    field: keyof T,
    errors: Errors<T>,
    translations: Translations,
    messageKey = 'Site.Prime.ErrorSubmit'
) {
    if (!data[field]) {
        errors[field] = translations[messageKey] as any;
    }
}

function validateFileRequired<T extends Record<string, any>>(
    data: T,
    field: keyof T,
    errors: Errors<T>,
    translations: Translations,
    messageKey: string
) {
    if (!data[field]) {
        errors[field] = translations[messageKey] as any;
    }
}

/* ===========================
   FORMS
   =========================== */

export const validateFPIEForm = (
    data: IFPIEFormData,
    translations: Translations
): ValidationResult<IFPIEFormData> => {
    const errors: Errors<IFPIEFormData> = {};
    setRequiredErrors(
        data,
        [
            'name',
            'surname',
            'denominationCompany',
            'numberCompany',
            'socialCompany',
            'addressStreet',
            'addressNumber',
            'addressPostal',
            'addressLocality',
            'iban',
            'sectorCompany',
            'commissionCompany',
            'bankAttestationFile',
        ],
        errors,
        translations
    );

    validateEmailPair(data, errors, translations);
    validatePhonePair(data, errors, translations);
    validateNiss(data, 'nationalRegisterTrainee', errors, translations);
    validateCommissionCompany(data, errors, translations);
    validateIbanRequired(data, errors, translations);
    validateCheckboxRequired(data, 'dataAreCorrect', errors, translations);
    validateFileRequired(
        data,
        'bankAttestationFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );

    return errors;
};

export const validateHandicapForm = (
    data: IHandicapFormData,
    translations: Translations
): ValidationResult<IHandicapFormData> => {
    const errors: Errors<IHandicapFormData> = {};

    setRequiredErrors(
        data,
        [
            'name',
            'surname',
            'denominationCompany',
            'numberCompany',
            'socialCompany',
            'addressStreet',
            'addressNumber',
            'addressPostal',
            'addressLocality',
            'mail',
            'mailConfirmation',
            'phone',
            'phoneConfirmation',
            'iban',
            'sectorCompany',
            'workCompany',
            'commissionCompany',
            'workerNiss',
            'workerName',
            'workerSurname',
            'employmentContractFile',
            'bankAttestationFile',
        ],
        errors,
        translations
    );

    validateEmailPair(data, errors, translations);
    validatePhonePair(data, errors, translations);
    validateNiss(data, 'workerNiss', errors, translations);
    validateCommissionCompany(data, errors, translations);
    validateIbanRequired(data, errors, translations);
    validateCheckboxRequired(data, 'dataAreCorrect', errors, translations);

    validateFileRequired(
        data,
        'bankAttestationFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );
    validateFileRequired(
        data,
        'employmentContractFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );

    return errors;
};

export const validateIncitantForm = (
    data: IIncitantFormData,
    translations: Translations
): ValidationResult<IIncitantFormData> => {
    const errors: Errors<IIncitantFormData> = {};

    setRequiredErrors(
        data,
        [
            'name',
            'surname',
            'denominationCompany',
            'numberCompany',
            'socialCompany',
            'addressStreet',
            'addressNumber',
            'addressPostal',
            'addressLocality',
            'mail',
            'mailConfirmation',
            'phone',
            'phoneConfirmation',
            'iban',
            'sectorCompany',
            'workCompany',
            'commissionCompany',
            'trainingName',
            'trainingRegistrationDate',
            'workerNiss',
            'workerName',
            'workerSurname',
        ],
        errors,
        translations
    );

    validateEmailPair(data, errors, translations);
    validatePhonePair(data, errors, translations);
    validateNiss(data, 'workerNiss', errors, translations);
    validateCommissionCompany(data, errors, translations);
    validateIbanRequired(data, errors, translations);
    validateCheckboxRequired(data, 'dataAreCorrect', errors, translations);

    validateFileRequired(
        data,
        'bankAttestationFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );
    validateFileRequired(
        data,
        'employmentContractFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );
    validateFileRequired(
        data,
        'trainingInvoiceFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );
    validateFileRequired(
        data,
        'trainingPaymentFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );

    return errors;
};

export const validateIndependantForm = (
    data: IIndependantFormData,
    translations: Translations
): ValidationResult<IIndependantFormData> => {
    const errors: Errors<IIndependantFormData> = {};

    setRequiredErrors(
        data,
        [
            'name',
            'surname',
            'nationalRegisterApplicant',
            'addressStreetApplicant',
            'addressNumberApplicant',
            'addressPostalApplicant',
            'addressLocalityApplicant',
            'numberCompany',
            'addressStreet',
            'addressNumber',
            'addressPostal',
            'addressLocality',
            'mail',
            'mailConfirmation',
            'phone',
            'phoneConfirmation',
            'sectorCompany',
            'workCompany',
            'supportStructure',
            'iban',
            'affiliationProofFile',
            'aceAttestationFile',

            'notIndependentPastTwoYears',
            'noConvention',
            'noTremplin',
            'hasAgreedToObligations',
        ],
        errors,
        translations
    );

    validateEmailPair(data, errors, translations);
    validatePhonePair(data, errors, translations);
    validateIbanRequired(data, errors, translations);
    validateNiss(data, 'nationalRegisterApplicant', errors, translations);
    const numberCompany = data.numberCompany.replace(/\D/g, '');

    if (!/^\d{10}$/.test(numberCompany)) {
        errors.numberCompany =
            translations['Site.Prime.IdentificationCodeError'];
    }
    validateCheckboxRequired(data, 'dataAreCorrect', errors, translations);
    validateCheckboxRequired(
        data,
        'notIndependentPastTwoYears',
        errors,
        translations
    );
    validateCheckboxRequired(data, 'noConvention', errors, translations);
    validateCheckboxRequired(data, 'noTremplin', errors, translations);
    validateCheckboxRequired(
        data,
        'hasAgreedToObligations',
        errors,
        translations
    );

    validateFileRequired(
        data,
        'aceAttestationFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );
    validateFileRequired(
        data,
        'affiliationProofFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );

    return errors;
};

export const validateJeuneForm = (
    data: IJeuneFormData,
    translations: Translations
): ValidationResult<IJeuneFormData> => {
    const errors: Errors<IJeuneFormData> = {};

    setRequiredErrors(
        data,
        [
            'name',
            'surname',
            'niss',
            'addressStreet',
            'addressNumber',
            'addressPostal',
            'addressLocality',
            'mail',
            'mailConfirmation',
            'phone',
            'phoneConfirmation',
            'iban',
            'schoolAttestationFile',
            'dataAreCorrect',

            'nameLegalRepresentative',
            'surnameLegalRepresentative',
            'addressStreetLegalRepresentative',
            'addressNumberLegalRepresentative',
            'addressPostalLegalRepresentative',
            'addressLocalityLegalRepresentative',
        ],
        errors,
        translations
    );

    if (
        data.ibanOwner === IbanOwnerType.OwnerLegalRepresentant &&
        !trimStr(data.legalRepresentantName)
    ) {
        errors.legalRepresentantName = translations['Site.Prime.ErrorSubmit'];
    }

    validateEmailPair(data, errors, translations);
    validatePhonePair(data, errors, translations);
    validateNiss(data, 'niss', errors, translations);

    return errors;
};

export const validateTuteurForm = (
    data: ITuteurFormData,
    translations: Translations
): ValidationResult<ITuteurFormData> => {
    const errors: Errors<ITuteurFormData> = {};

    setRequiredErrors(
        data,
        [
            'denominationCompany',
            'socialCompany',
            'name',
            'surname',
            'numberCompany',
            'addressStreet',
            'addressNumber',
            'addressPostal',
            'addressLocality',
            'mail',
            'mailConfirmation',
            'phone',
            'phoneConfirmation',
            'commissionCompany',
            'schoolAttestationFile',
            'dataAreCorrect',
            'iban',
            'sectorCompany',
            'nationalRegisterTutor',
            'nationalRegisterTrainee',
            'bankAttestationFile',
        ],
        errors,
        translations
    );

    validateEmailPair(data, errors, translations);
    validatePhonePair(data, errors, translations);
    validateCommissionCompany(data, errors, translations);
    validateIbanRequired(data, errors, translations);
    validateNiss(data, 'nationalRegisterTrainee', errors, translations);
    validateNiss(data, 'nationalRegisterTutor', errors, translations);
    validateFileRequired(
        data,
        'bankAttestationFile',
        errors,
        translations,
        'Site.Prime.ErrorSubmit'
    );

    return errors;
};
